<script setup lang="ts">
import { useInvoices } from '@/composables/rc/invoices'
import { InvoiceStatus, type IInvoice } from '@/types/rc/invoices'
import { Icon } from '@iconify/vue'
import { ref, watchEffect } from 'vue'

const props = withDefaults(defineProps<{ invoice: IInvoice; expanded: boolean; editable?: boolean }>(), {
  editable: true,
})
const emit = defineEmits(['export', 'delete', 'refresh'])

const { updateInvoiceMutation } = useInvoices()

// Status update:

const { mutate: updateStatus, isPending: statusUpdatePending } = updateInvoiceMutation

const isStatusSelectActive = ref(false)
const selectedStatus = ref<InvoiceStatus>()
const onStatusChange = () => {
  if (!selectedStatus.value) return
  updateStatus(
    { id: props.invoice.id, payload: { status: selectedStatus.value } },
    {
      onSettled: () => {
        isStatusSelectActive.value = false
      },
    }
  )
}

// Sync status with BE (fed via props by the parent InvoiceCard)

watchEffect(() => {
  selectedStatus.value = props.invoice?.status as InvoiceStatus
})
</script>

<template>
  <header class="inline-flex gap-6 cursor-auto">
    <div class="w-full inline-flex items-center text-left gap-4 justify-between">
      <!-- Id -->
      <h4 class="w-32">Invoice #{{ invoice.id }}</h4>

      <slot />

      <!-- Status badge -->
      <div v-if="!isStatusSelectActive" class="w-48 flex gap-1 justify-end">
        <StatusBadge :status="invoice.status" @click.stop />

        <el-button v-if="editable" link @click.stop.prevent="isStatusSelectActive = true">
          <Icon icon="material-symbols:edit-outline" :height="20" />
        </el-button>
      </div>

      <!-- Status select -->
      <div v-if="isStatusSelectActive" class="w-48 flex gap-1 justify-end">
        <el-select
          v-model="selectedStatus"
          :disabled="statusUpdatePending"
          :loading="statusUpdatePending"
          @click.stop
          @change="onStatusChange"
        >
          <el-option v-for="opt in Object.values(InvoiceStatus)" :key="opt" :label="opt" :value="opt" />
        </el-select>

        <el-button :disabled="statusUpdatePending" link @click.stop.prevent="isStatusSelectActive = false">
          <Icon icon="material-symbols:close" :height="20" />
        </el-button>
      </div>
    </div>

    <!-- Controls - pushed to the right -->
    <div class="shrink-0">
      <el-button v-if="editable" title="Refresh" link @click.stop="emit('refresh', invoice.id)">
        <Icon icon="material-symbols:refresh" height="20" :inline="true" />
      </el-button>
      <el-button title="Export" link @click.stop="emit('export', invoice.id)">
        <Icon icon="material-symbols:download" height="20" :inline="true" />
      </el-button>
      <el-button v-if="editable" title="Delete" link @click.stop="emit('delete', invoice.id)">
        <Icon icon="material-symbols:delete-outline" height="20" :inline="true" />
      </el-button>
    </div>
  </header>
</template>

<style scoped lang="scss">
header {
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  color: var(--text-secondary);

  h4 {
    font-size: var(--font-size-body-compact);
  }
}
</style>
